import type { Project } from '~/utils/project/Project'
import type { RequestSimulationOpportunities } from '~/utils/simulation/Simulation'
import { resolveDefaultDueDay } from '~/utils/simulation/dueDay'

/**
 * Utilities for project manipulation
 */

/**
 * Sets default values for a complete project
 * @param project Complete project
 * @param isEditMode Se está em modo edit (com query ?edit)
 * @returns Object with configured default values
 */
export function extractProjectDefaults(
  project: Project,
  isEditMode: boolean = false,
  useDynamicDefault: boolean = false,
) {
  return {
    commission: project.selected_installments.commission,
    commissionRaw: project.selected_installments?.commission_raw || 0,
    gracePeriod: project.selected_installments.grace_period,
    dueDay: resolveDefaultDueDay(
      isEditMode
        ? project.selected_installments.due_day
        : (project.due_day ?? project.selected_installments.due_day),
      {
        isEditMode,
        isComplete: Boolean(project.complete),
        useDynamicDefault,
      },
    ),
    firstDueDate: isEditMode
      ? project.selected_installments.first_due_date
      : (project.first_due_date ?? project.selected_installments.first_due_date),
    installment: project.selected_installments.installments,
    isFinished: project.complete,
    cet: project?.selected_installments?.cet,
  }
}

/**
 * Sets default values for an incomplete project
 * @param project Project (optional)
 * @param lastSimulation Last simulation
 * @returns Object with configured default values
 */
export function extractOldSimulationDefaults(
  project?: Project,
  lastSimulation?: any,
  useDynamicDefault: boolean = false,
) {
  if (!project || !lastSimulation) {
    return null
  }

  const defaults = {
    commission: lastSimulation.commission || 0,
    commissionRaw: lastSimulation?.commission_raw || 0,
    gracePeriod: lastSimulation.grace_period || 1,
    dueDay: resolveDefaultDueDay(
      project.due_day ?? lastSimulation?.due_day,
      { isComplete: false, useDynamicDefault },
    ),
    firstDueDate: project.first_due_date ?? lastSimulation?.first_due_date ?? '',
    installment: lastSimulation.installments || 60,
    isFinished: project.complete,
  }

  console.warn('🔍 extractOldSimulationDefaults:', {
    projectId: project?.id,
    lastSimulationInstallments: lastSimulation.installments,
    finalInstallment: defaults.installment,
    lastSimulation,
  })

  return defaults
}

/**
 * Calculates default installments for an incomplete project
 * @param project Project
 * @returns Default installments value
 */
export function calculateDefaultInstallmentForIncompleteProject(project: Project): number {
  // Uses the same logic as the original code: calculates installments array and gets the middle value
  const installmentsSteps = 12
  const min = project.min_installments || 0
  const max = project.max_installments
  const installments = Array.from({ length: (max - min) / installmentsSteps + 1 }, (_, i) => min + i * installmentsSteps)
  const defaultInstallment = installments[Math.floor(installments.length / 2)]

  return defaultInstallment
}

/**
 * Creates payload for simulation opportunities
 * @param project Project
 * @returns Payload for API
 */
export function createSimulationOpportunitiesPayload(project: Project): RequestSimulationOpportunities {
  return {
    partner_id: project.partner_id,
    financing_value: project.project_value - project.down_payment,
    project_type: project.person_type,
  }
}

/**
 * Checks if project is inactive
 * @param project Project
 * @returns true if project is inactive
 */
export function isProjectInactive(project: Project): boolean {
  return project?.project_status === 'INACTIVE' && !project.complete
}

/**
 * Checks if project is complete
 * @param project Project
 * @returns true if project is complete
 */
export function isProjectComplete(project: Project): boolean {
  return project && project.complete
}
